import React, { useRef } from 'react';

import { MDText } from 'i18n-react';

import { useBreakpoint } from 'Components/misc/Breakpoints/Breakpoints';
import StickyVRM from 'Components/misc/StickyVRM/StickyVRM';
import DialogLoader from 'Components/transitions/DialogLoader/DialogLoader';
import { Content } from 'LegacySBCRA/Content/Content';

import Status404 from '../../error/404';
import LocalTexts from '../wordpress/wordpress.json';

import { ContentRouter } from './components/ContentRouter/ContentRouter';
import { Header } from './components/Header/Header';
import { Sidebar } from './components/Sidebar/Sidebar';
import { useBreadcrumbNavigation } from './hooks/useBreadcrumbNavigation';
import { useContentType } from './hooks/useContentType';
import { useTimers } from './hooks/useTimers';
import { useWPData } from './hooks/useWPData';
import { ContentType } from './utilities/WordpressConstants';

import styles from './WordpressPages.scss';

const LocalT = new MDText(LocalTexts);

const WordpressPages: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);

  const { data, empty, isGuide, isSSR, ready, ...rest } = useWPData();
  const { fetchBlogTitleAndTopNav } = useBreadcrumbNavigation();
  const { contentType } = useContentType({ data, empty, ...rest });
  const { stickyVRM } = useTimers();
  const breakpoints = useBreakpoint();
  const tabletBreakpoint = breakpoints.maxWidth.breakpointTablet;

  if (!ready) {
    return <DialogLoader />;
  }

  if (contentType === ContentType.ERROR) {
    return <Status404 />;
  }

  const { blogTitle: pageTitle, topNav } = fetchBlogTitleAndTopNav();

  return (
    <Content ref={contentRef} className={styles.view}>
      <section>
        {tabletBreakpoint && stickyVRM && contentRef.current && (
          <StickyVRM $content={contentRef.current} />
        )}
        <div className={styles.content} id="content">
          <Header {...{ pageTitle, topNav }} />
          <ContentRouter {...{ contentType, data, isSSR }} />
          {
            isGuide && (
              <p className={styles.disclaimer}>{LocalT.translate('disclaimer')}</p>
            )
          }
        </div>
        <Sidebar {...{ contentType, data }} />
      </section>
    </Content>
  );
};

export default WordpressPages;
